import { AfterViewInit, Component, Inject, Input, OnInit, Renderer2 } from '@angular/core';
import { CurrencyPipe, DOCUMENT, Location } from '@angular/common';
import { Filters, SearchRequest } from '@schemas/search/search-request';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AppConfiguration } from '@schemas/app.interface';
import { AppService } from '@services/app/app.service';
import { SearchService } from '../../../../../core/services/search/search.service';
import { ActivatedRoute, Data, Params, Router } from '@angular/router';
import { FilterBoxInputTypeEnum } from './filter-box/filter-box.component';
import { ConditionDialogComponent } from '../../dialogs/condition-dialog/condition-dialog.component';
import { DialogService } from '@services/app/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { ShoesSizeDialogComponent } from '../../dialogs/shoes-size-popup-dialog/shoes-size-popup-dialog.component';
import { ClothesSizeDialogComponent } from '../../dialogs/clothes-size-popup-dialog/clothes-size-popup-dialog.component';
import { ChangeContext, Options } from '@angular-slider/ngx-slider';
import { ScreenDetectorBootstrapService } from '@services/app/screen-detector-bootstrap.service';

const CATEGORY_FILTER_NAME = 'category';
const SUBCATEGORY_FILTER_NAME = 'sub';
const CONDITION_FILTER_NAME = 'condition';

@Component({
  selector: 'tvb-filters-section',
  templateUrl: './filters-section.component.html',
  styleUrls: ['./filters-section.component.scss'],
})
export class FiltersSectionComponent implements OnInit {
  @Input() initExpanded: boolean = true;
  public categoryExpanded?: boolean = null;
  public subExpanded?: boolean = null;
  public colorExpanded?: boolean = null;
  public brandExpanded?: boolean = null;
  public styleExpanded?: boolean = null;
  public conditionExpanded?: boolean = null;
  public priceExpanded?: boolean = null;
  public discountExpanded?: boolean = null;
  public shoesExpanded?: boolean = null;
  public clothingExpanded?: boolean = null;
  public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public sections: any;
  public searchRequest: SearchRequest;
  public appConfig: AppConfiguration;
  public FilterBoxInputTypeEnum = FilterBoxInputTypeEnum;
  public routeDataParams: Data;
  private routePathParams: Params;
  private routeQueryParams: Params;
  private originalPath: string;
  private functionMappingForOptionToggling = {
    category: this.onToggleCategoryOption.bind(this),
    sub: this.onToggleSubCategoryOption.bind(this),
    color: this.onToggleOption.bind(this),
    brand: this.onToggleOption.bind(this),
    style: this.onToggleOption.bind(this),
    condition: this.onToggleOption.bind(this),
    price: this.onPriceToggleOption.bind(this),
    discount: this.onToggleOption.bind(this),
    shoes: this.onToggleOption.bind(this),
    clothing: this.onToggleOption.bind(this),
  };
  private conditionsDictionary;
  public discountOptions: Array<{ discountName: string; discountValue: string }>;
  public priceOptions: Array<{ title: string; minValue: number | string; maxValue: number | string; price: string }>;
  private pricesDictionary: any;
  public priceSliderOptions: Options = { floor: 0, ceil: 99999, step: 1 };
  public selectedMinPrice: number = 0;
  public selectedMaxPrice: number = 100;
  private keepMenu: boolean = false;

  constructor(
    private searchService: SearchService,
    public appService: AppService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialogModalService: DialogService,
    private translate: TranslateService,
    private currencyPipe: CurrencyPipe,
    private location: Location,
    private renderer: Renderer2,
    private screenSizeDetector: ScreenDetectorBootstrapService,
    @Inject(DOCUMENT) private doc: Document
  ) {
    this.translate.get(['']).subscribe(translations => {
      this.conditionsDictionary = {
        1: this.translate.instant('New with tags'),
        2: this.translate.instant('Excellent'),
        4: this.translate.instant('Good but used'),
        5: this.translate.instant('Worn with love'),
      };
      this.discountOptions = [
        { discountName: '10' + this.translate.instant('% off or more'), discountValue: '10' },
        { discountName: '20' + this.translate.instant('% off or more'), discountValue: '20' },
        { discountName: '30' + this.translate.instant('% off or more'), discountValue: '30' },
        { discountName: '40' + this.translate.instant('% off or more'), discountValue: '40' },
        { discountName: '50' + this.translate.instant('% off or more'), discountValue: '50' },
      ];

      if (this.searchService.menuMobileOpen.value?.top)
        window.scrollTo({ top: this.searchService.menuMobileOpen.value.top, behavior: 'auto' });

      this.sections = this.searchService.filters.value ?? null;
    });
  }

  public async ngOnInit(): Promise<void> {
    this.isMobile$ = this.screenSizeDetector.isMobile;
    this.routeDataParams = this.activatedRoute.snapshot.data;
    this.routePathParams = this.activatedRoute.snapshot.params;
    this.routeQueryParams = this.activatedRoute.snapshot.queryParams;
    this.appService.getAppConfigurationBehavior().subscribe((val: AppConfiguration) => {
      this.appConfig = val;

      const under_i18n: string = this.translate.instant('Under');
      const over_i18n: string = this.translate.instant('Over');

      this.pricesDictionary = {
        eur: [
          this.generatePriceOption(0, 50, under_i18n + ' '),
          this.generatePriceOption(50, 300, null),
          this.generatePriceOption(300, 500, null),
          this.generatePriceOption(500, 1000, null),
          this.generatePriceOption(1000, undefined, over_i18n + ' '),
        ],
        usd: [
          this.generatePriceOption(0, 100, under_i18n + ' '),
          this.generatePriceOption(100, 400, null),
          this.generatePriceOption(400, 600, null),
          this.generatePriceOption(600, 1200, null),
          this.generatePriceOption(1200, undefined, over_i18n + ' '),
        ],
        dkk: [
          this.generatePriceOption(0, 400, under_i18n + ' '),
          this.generatePriceOption(400, 2000, null),
          this.generatePriceOption(2000, 4000, null),
          this.generatePriceOption(4000, 7500, null),
          this.generatePriceOption(7500, undefined, over_i18n + ' '),
        ],
        gbp: [
          this.generatePriceOption(0, 50, under_i18n + ' '),
          this.generatePriceOption(50, 300, null),
          this.generatePriceOption(300, 500, null),
          this.generatePriceOption(500, 1000, null),
          this.generatePriceOption(1000, undefined, over_i18n + ' '),
        ],
        sek: [
          this.generatePriceOption(0, 600, under_i18n + ' '),
          this.generatePriceOption(600, 2500, null),
          this.generatePriceOption(2500, 5500, null),
          this.generatePriceOption(550, 9500, null),
          this.generatePriceOption(9500, undefined, over_i18n + ' '),
        ],
      };
      this.priceOptions = this.pricesDictionary[this.appConfig.currencyCode.toLowerCase()];
    });

    await this.getSectionsFilters();
    this.applyMenuSettings();

    if ('parent_category_seo_url' in this.routePathParams) {
      this.originalPath = this.routePathParams['parent_category_seo_url'];
    }

    if ('price' in this.routeQueryParams) {
      this.setMinMaxPriceBasedOnStringPattern(this.routeQueryParams['price']);
    }

    this.searchService.searchRequest.subscribe(val => (this.searchRequest = val));
  }

  applyMenuSettings() {
    switch (this.searchService.menuMobileOpen.value.menu) {
      case 'category':
        this.categoryExpanded = true;
        break;
      case 'sub':
        this.subExpanded = true;
        break;
      case 'style':
        this.styleExpanded = true;
        break;
      case 'brand':
        this.brandExpanded = true;
        break;
      case 'condition':
        this.conditionExpanded = true;
        break;
      case 'color':
        this.colorExpanded = true;
        break;
      case 'price':
        this.priceExpanded = true;
        break;
      case 'discount':
        this.discountExpanded = true;
        break;
      case 'shoes':
        this.shoesExpanded = true;
        break;
      case 'clothing':
        this.clothingExpanded = true;
        break;
    }
  }

  public async getSectionsFilters(): Promise<void> {
    this.sections = await firstValueFrom(
      this.searchService.getSectionFilters({
        page: 0,
        pageSize: 60,
        filters: mountSectionFiltersParams.call(this),
        sorting: null,
        currency: this.appConfig?.currencyCode || 'eur',
        language: this.appConfig?.languageShortName || 'en',
      })
    );

    // Remove empty values from styles section
    if (this.sections && this.sections?.styles) {
      for (let i = 0; i < this.sections.styles.length; i++) {
        if (this.sections.styles[i]['styleUrl'] === '' || this.sections.styles[i]['styleName'] === '') {
          this.sections.styles.splice(i, 1);
        }
      }
    }

    this.priceSliderOptions.floor = this.sections.forYourBudget['min' + this.appConfig.currencyCode.toUpperCase()];
    this.priceSliderOptions.ceil = this.sections.forYourBudget['max' + this.appConfig.currencyCode.toUpperCase()];
    this.selectedMinPrice = this.priceSliderOptions.floor;
    this.selectedMaxPrice = this.priceSliderOptions.ceil;
    parseConditions.call(this);

    // AUXILIARY FUNCTIONS
    function mountSectionFiltersParams() {
      return this.searchService.searchRequest.value.filters.map((f: Filters) => ({
        field: f.field,
        value: f.value,
        operator: f.operator || '==',
      }));
    }

    function parseConditions(this) {
      this.sections['conditions'] = this.sections['conditions']?.map(condition => {
        return {
          ...condition,
          conditionName: this.conditionsDictionary[condition.conditionRating],
        };
      });
    }
  }

  public handleOptionToggling(outputEvent: {
    filterName: string;
    filterValue: string;
    filterOperator: string;
    inputType: FilterBoxInputTypeEnum;
    filterId?: string;
  }): void {
    this.renderer.removeClass(this.doc.body, 'noVerticalScroll');
    this.clearPageFilterValue();
    this.functionMappingForOptionToggling[outputEvent.filterName](
      outputEvent.filterName,
      outputEvent.filterValue,
      outputEvent.inputType,
      outputEvent.filterOperator
    );
    this.searchService.menuMobileOpen.next({
      open: true,
      menu: outputEvent.filterName,
      top: this.doc.documentElement.scrollTop,
    });
    this.searchService.filters.next(this.sections);
    this.keepMenu = true;
  }

  private clearPageFilterValue(): void {
    let destiny = window.location.pathname + window.location.search;
    const pageQueryParam = window.location.search.match(/[?&]page=\d+/);

    if (pageQueryParam) {
      destiny = destiny.replace(pageQueryParam[0], '');
    }

    this.location.replaceState(destiny);
  }

  public removeTag(tag) {
    this.searchService.menuMobileOpen.next({ open: false, menu: null, top: null });
    this.keepMenu = false;
    this.clearPageFilterValue();

    switch (tag.field) {
      case CATEGORY_FILTER_NAME:
        this.onToggleCategoryOption(tag.field, tag.value);
        break;
      case SUBCATEGORY_FILTER_NAME:
        this.onToggleSubCategoryOption(tag.field, tag.value);
        break;
      case CONDITION_FILTER_NAME:
        this.onToggleOption(tag.field, tag.value, FilterBoxInputTypeEnum.CHECKBOX);
        break;
      default:
        this.onToggleOption(tag.field, tag.value, FilterBoxInputTypeEnum.CHECKBOX);
        break;
    }
  }

  public onToggleCategoryOption(filterName: string, filterValue: string): void {
    const searchRequestSnapshot = this.searchService.searchRequest.value;
    const searchFiltersSnapshot = searchRequestSnapshot.filters.filter(x => x.field === 'category');
    const shouldRemoveOption = !!searchFiltersSnapshot.find(_ => _.value === filterValue);
    let urlPath = window.location.pathname;
    let urlSearchPath = window.location.search || '';

    if (shouldRemoveOption) {
      const queryFirstOperator = searchFiltersSnapshot.length == 1 ? '?' : '&';

      if (filterName in this.routeQueryParams && this.routeQueryParams[filterName] === filterValue) {
        urlSearchPath = urlSearchPath.replace('?' + filterName + '=' + filterValue, '');
        urlSearchPath = urlSearchPath.replace('&' + filterName + '=' + filterValue, '');
      }

      if (
        'parent_category_seo_url' in this.routePathParams
        && this.routePathParams['parent_category_seo_url'].includes(
          searchFiltersSnapshot.find(x => x.field === 'category').value
        )
        && filterName === 'category'
      ) {
        if (searchFiltersSnapshot.length == 1) {
          urlSearchPath = urlSearchPath.replace(queryFirstOperator + filterName + '=' + filterValue, '');
          urlPath = urlPath.replace('/' + filterValue, '');

          if (searchRequestSnapshot.filters.filter(x => x.field === 'sub')[0]) {
            urlPath = urlPath.replace('/' + searchRequestSnapshot.filters.filter(x => x.field === 'sub')[0].value, '');
          }

          if (searchRequestSnapshot.filters.filter(x => x.field !== 'category').length > 0) {
            const filters = searchRequestSnapshot.filters.filter(x => x.field !== 'category');

            filters.every(filter => {
              if (urlSearchPath.includes('?' + filter.field + '=' + filter.value)) return false;

              if (urlSearchPath.includes('&' + filter.field + '=' + filter.value)) {
                urlSearchPath = urlSearchPath.replace(
                  '&' + filter.field + '=' + filter.value,
                  '?' + filter.field + '=' + filter.value
                );

                return false;
              }

              return true;
            });
          }
        }

        if (searchFiltersSnapshot.length == 2) {
          if (this.routePathParams['parent_category_seo_url'] === filterValue) {
            if (searchFiltersSnapshot[1].field == 'category') {
              urlPath = urlPath.replace(searchFiltersSnapshot[0].value, searchFiltersSnapshot[1].value);
            }
            else {
              urlPath = urlPath.replace('/' + searchFiltersSnapshot[0].value, '');
            }

            if (searchFiltersSnapshot[1].field == 'sub') {
              urlPath = urlPath.replace('/' + searchFiltersSnapshot[1].value, '');
            }

            urlSearchPath = urlSearchPath.replace('?' + filterName + '=' + filterValue, '');
          }
          else {
            urlSearchPath = urlSearchPath.replace(queryFirstOperator + filterName + '=' + filterValue, '');
          }

          urlSearchPath = '?' + urlSearchPath.substring(1);
        }

        if (searchFiltersSnapshot.length >= 3) {
          if (this.routePathParams['parent_category_seo_url'] === searchFiltersSnapshot[0].value) {
            urlSearchPath = urlSearchPath.replace('?' + filterName + '=' + filterValue, '');
            urlSearchPath = urlSearchPath.replace('&' + filterName + '=' + filterValue, '');
            urlSearchPath = '?' + urlSearchPath.substring(1);
          }

          if (this.routePathParams['parent_category_seo_url'] !== searchFiltersSnapshot[1].value) {
            if (
              searchFiltersSnapshot[0].field == 'category'
              && this.routePathParams['parent_category_seo_url'] === filterValue
            ) {
              urlPath = urlPath.replace(searchFiltersSnapshot[0].value, searchFiltersSnapshot[1].value);
            }

            for (let i = 0; i < searchFiltersSnapshot.length; i++) {
              if (searchFiltersSnapshot[i].field !== 'category')
                urlPath = urlPath.replace('/' + searchFiltersSnapshot[1].value, '');
            }
          }
        }
      }
      else {
        if (searchRequestSnapshot.filters.filter(x => x.field === 'sub')[0]) {
          urlPath = urlPath.replace('/' + searchRequestSnapshot.filters.filter(x => x.field === 'sub')[0].value, '');
        }

        urlSearchPath = urlSearchPath.replace(`${queryFirstOperator}${filterName}=${filterValue}`, '');
      }

      const newFilters = searchRequestSnapshot.filters.filter(_ => !(_.value == filterValue));

      searchRequestSnapshot.filters = newFilters;
      this.searchService.searchRequest.next(searchRequestSnapshot);
    }
    else {
      if (searchFiltersSnapshot.length == 0) {
        if (
          !this.routePathParams['parent_category_seo_url']
          && this.routePathParams[filterName] !== filterValue
          && filterName === 'category'
          && !this.routePathParams?.search_query
        ) {
          urlPath += '/' + filterValue;
        }

        if (!this.hasQueryParams(urlSearchPath)) urlSearchPath += '?' + filterName + '=' + filterValue;
        else urlSearchPath += '&' + filterName + '=' + filterValue;
      }
      else if (searchFiltersSnapshot.length == 1) {
        if ('parent_category_seo_url' in this.routePathParams && searchFiltersSnapshot[0].field === 'category') {
          if (!window.location.search.includes('?category=' + this.routePathParams['parent_category_seo_url'])) {
            urlSearchPath += '?category=' + this.routePathParams['parent_category_seo_url'];
          }

          urlSearchPath += '&' + filterName + '=' + filterValue;
        }
      }
      else {
        urlSearchPath += '&' + filterName + '=' + filterValue;
      }

      searchRequestSnapshot.filters.push({
        field: filterName,
        value: filterValue,
        operator: '==',
      });
      this.searchService.searchRequest.next(searchRequestSnapshot);
    }

    this.router.navigateByUrl((urlPath + urlSearchPath).toLowerCase());
  }

  public onToggleSubCategoryOption(filterName: string, filterValue: string): void {
    const searchRequestSnapshot = this.searchService.searchRequest.value;
    const searchFiltersSnapshot = searchRequestSnapshot.filters;
    const subCategoriesInSearchFilters = searchFiltersSnapshot.filter(_ => _.field === filterName) || [];
    const shouldRemoveOption = !!subCategoriesInSearchFilters.find(_ => _.value === filterValue);
    let urlPath = window.location.pathname;
    let urlSearchPath = window.location.search || '';

    if (shouldRemoveOption) {
      if (this.routeQueryParams[filterName]?.includes(filterValue)) {
        urlSearchPath = urlSearchPath.replace('&' + filterName + '=' + filterValue, '');

        if (
          !('child_category_seo_url' in this.routePathParams)
          || !this.routePathParams['child_category_seo_url'].includes(filterValue)
        ) {
          urlSearchPath = urlSearchPath.replace(filterName + '=' + filterValue + '&', '');
          urlSearchPath = urlSearchPath.replace(filterName + '=' + filterValue, '');
        }
      }

      if (
        'child_category_seo_url' in this.routePathParams
        && this.routePathParams['child_category_seo_url'].includes(filterValue)
      ) {
        if (typeof this.routeQueryParams[filterName] === 'string') {
          urlPath = urlPath.replace('/' + filterValue, '');
        }

        if (Array.isArray(this.routeQueryParams[filterName])) {
          urlPath = urlPath.replace(
            '/' + filterValue,
            '/' + this.routeQueryParams[filterName].find(_ => _ !== filterValue)
          );
        }
      }

      searchRequestSnapshot.filters = searchFiltersSnapshot.filter(_ => !(_.value == filterValue));
      this.searchService.searchRequest.next(searchRequestSnapshot);
    }
    else {
      if (subCategoriesInSearchFilters.length === 0) {
        if (!this.routePathParams?.search_query) {
          if (!this.routePathParams['child_category_seo_url']) {
            urlPath += '/' + filterValue;

            if (!this.routeQueryParams['category']) {
              urlSearchPath += '?category=' + this.routePathParams['parent_category_seo_url'];
            }
          }
        }
        else if (!this.hasQueryParams(urlSearchPath)) urlSearchPath += '?' + filterName + '=' + filterValue;
        else urlSearchPath += '&' + filterName + '=' + filterValue;
      }
      else if (
        subCategoriesInSearchFilters.length === 1
        && this.routePathParams['child_category_seo_url']
        && !this.routeQueryParams['category']
      ) {
        urlSearchPath += '?' + filterName + '=' + filterValue;
      }
      else {
        urlSearchPath += '&' + filterName + '=' + filterValue;
      }

      searchRequestSnapshot.filters.push({
        field: filterName,
        value: filterValue,
        operator: '==',
      });
      this.searchService.searchRequest.next(searchRequestSnapshot);
    }

    this.router.navigateByUrl((urlPath + urlSearchPath).toLowerCase());
  }

  public onToggleOption(
    filterName: string,
    filterValue: string,
    inputType: FilterBoxInputTypeEnum,
    filterOperator = '=='
  ): void {
    filterName = filterName.toLowerCase();
    filterValue = filterValue.toLowerCase();
    const searchRequestSnapshot: SearchRequest = this.searchService.searchRequest.value;
    const searchFiltersSnapshot: Filters[] = searchRequestSnapshot.filters;
    const occurrencesInFilterRequest: Filters[]
      = searchFiltersSnapshot.filter(_ => _.field.toLowerCase() === filterName.toLowerCase()) || [];
    const shouldRemoveOption: boolean = !!occurrencesInFilterRequest.find(_ => _.value === filterValue);
    const urlPath: string = window.location.pathname;
    let urlSearchPath: string = window.location.search || '';
    const urlSearchPathFirstParamValue = window.location.search
      ? window.location.search.replace('?', '').replace('%20', '-').split('&')[0].split('=')[1]?.toLowerCase()
      : '';

    switch (inputType) {
      case FilterBoxInputTypeEnum.CHECKBOX: {
        if (shouldRemoveOption) {
          removeParamFromUrl.call(this);
          fixUrlIfFirstParamWasRemoved();
          removeParamFromFilterRequest.call(this);
        }
        else {
          addParamInUrl();
          addNewParamIntoFilterRequest.call(this);
        }

        break;
      }
      case FilterBoxInputTypeEnum.RADIOBUTTON: {
        if (occurrencesInFilterRequest.length > 0) {
          urlSearchPath = urlSearchPath.replace(
            filterName + '=' + occurrencesInFilterRequest[0].value,
            filterName + '=' + filterValue
          );
          fixUrlIfFirstParamWasRemoved();
          replaceParamInFilterRequest.call(this);
          removeParamFromUrl.call(this);
        }
        else {
          addParamInUrl();
          addNewParamIntoFilterRequest.call(this);
        }

        break;
      }
      default:
        break;
    }

    this.router.navigateByUrl((urlPath + urlSearchPath).toLowerCase());

    // AUXILIARY FUNCTIONS
    function removeParamFromUrl() {
      const operatorToBeReplaced = filterValue === urlSearchPathFirstParamValue ? '?' : '&';

      if (this.routeQueryParams[filterName]?.includes(filterValue)) {
        urlSearchPath = urlSearchPath.replace(operatorToBeReplaced + filterName + '=' + filterValue, '');
      }
    }

    function fixUrlIfFirstParamWasRemoved() {
      if (filterValue === urlSearchPathFirstParamValue) {
        urlSearchPath = '?' + urlSearchPath.substring(1);
      }
    }

    function addParamInUrl() {
      const operatorToBeAdded = urlSearchPath.includes('?') ? '&' : '?';

      urlSearchPath += operatorToBeAdded + filterName + '=' + filterValue;
    }

    function addNewParamIntoFilterRequest() {
      searchRequestSnapshot.filters.push({
        field: filterName,
        value: filterValue,
        operator: filterOperator,
      });
      this.searchService.searchRequest.next(searchRequestSnapshot);
    }

    function removeParamFromFilterRequest() {
      searchRequestSnapshot.filters = searchFiltersSnapshot.filter(_ => !(_.value == filterValue));
      this.searchService.searchRequest.next(searchRequestSnapshot);
    }

    function replaceParamInFilterRequest() {
      searchRequestSnapshot.filters = searchFiltersSnapshot.map(filter => {
        if (filter.field === filterName) {
          filter.value = filterValue;

          return filter;
        }

        return filter;
      });
      this.searchService.searchRequest.next(searchRequestSnapshot);
    }
  }

  public onPriceToggleOption(
    filterName: string,
    filterValue: string,
    inputType: FilterBoxInputTypeEnum,
    filterOperator = '=='
  ) {
    this.setMinMaxPriceBasedOnStringPattern(filterValue);
    this.onToggleOption(filterName, filterValue, inputType, filterOperator);
  }

  public isAnyCategoryOrSubSelected(): boolean {
    return !!(
      this.searchService.searchRequest.value.filters.find(_ => _.field === 'category')
      || this.searchService.searchRequest.value.filters.find(_ => _.field === 'sub')
    );
  }

  public hasQueryParams(url: string): boolean {
    return url.includes('?');
  }

  public openConditionsInfo($event): void {
    $event.stopPropagation();
    this.dialogModalService.openAuthenticityPopup<ConditionDialogComponent>(ConditionDialogComponent);
  }

  public openShoesSizeInfo($event): void {
    $event.stopPropagation();
    this.dialogModalService.openAuthenticityPopup<ShoesSizeDialogComponent>(ShoesSizeDialogComponent);
  }

  public openClothingSizeInfo($event): void {
    $event.stopPropagation();
    this.dialogModalService.openAuthenticityPopup<ClothesSizeDialogComponent>(ClothesSizeDialogComponent);
  }

  public isThisSectionAvailable(section: string) {
    switch (section) {
      case 'brand':
        if (
          this.sections?.brands
          && this.routeDataParams?.plpFor !== 'designer'
          && this.routeDataParams?.plpFor !== 'style'
        )
          return true;

        return false;
      case 'category':
        if (this.sections?.categories && this.routeDataParams?.plpFor !== 'style') return true;

        return false;
      case 'sub':
        if (
          this.sections?.subCategories
          && this.isAnyCategoryOrSubSelected()
          && this.routeDataParams?.plpFor !== 'style'
        )
          return true;

        return false;
      case 'style':
        if (this.sections?.styles && this.routeDataParams?.plpFor !== 'style') return true;

        return false;
      case 'clothing':
        if (
          this.routePathParams['parent_category_seo_url'] === 'shoes'
          || this.routePathParams['parent_category_seo_url'] === 'bags'
          || this.routePathParams['parent_category_seo_url'] === 'accessories'
          || this.routePathParams['parent_category_seo_url'] === 'jewelry'
        )
          return false;

        return true;
      case 'shoes':
        if (
          this.routePathParams['parent_category_seo_url'] === 'clothing'
          || this.routePathParams['parent_category_seo_url'] === 'bags'
          || this.routePathParams['parent_category_seo_url'] === 'accessories'
          || this.routePathParams['parent_category_seo_url'] === 'jewelry'
        )
          return false;

        return true;
      default:
        return true;
    }
  }

  private getPriceTitle(preText: string, postText: string, minValue: number, maxValue: number): string {
    if (!minValue) {
      return (
        (preText || '')
        + this.currencyPipe.transform(maxValue, this.appConfig?.currencyCode, 'symbol', '1.0-0').replace(',', '')
      );
    }

    if (!maxValue) {
      return (
        (preText || '')
        + this.currencyPipe.transform(minValue, this.appConfig?.currencyCode, 'symbol', '1.0-0').replace(',', '')
      );
    }

    return (
      (preText || '')
      + this.currencyPipe.transform(minValue, this.appConfig?.currencyCode, 'symbol', '1.0-0').replace(',', '')
      + ' - '
      + this.currencyPipe.transform(maxValue, this.appConfig?.currencyCode, 'symbol', '1.0-0').replace(',', '')
      + (postText || '')
    );
  }

  private getPriceText(minValue: number, maxValue: number): string {
    return (minValue || '') + '-' + (maxValue || '');
  }

  private generatePriceOption(minValue: number, maxValue: number, preText: string): PriceOption {
    return {
      title: this.getPriceTitle(preText, null, minValue, maxValue),
      minValue,
      maxValue,
      price: this.getPriceText(minValue, maxValue),
    };
  }

  public handlePriceSliderChange(event: ChangeContext) {
    this.renderer.removeClass(this.doc.body, 'noVerticalScroll');
    this.selectedMinPrice = event.value;
    this.selectedMaxPrice = event.highValue;
    this.onPriceToggleOption(
      'price',
      this.selectedMinPrice + '-' + this.selectedMaxPrice,
      FilterBoxInputTypeEnum.RADIOBUTTON,
      '=='
    );
  }

  public formatPrice(price: number): string {
    return this.currencyPipe.transform(price, this.appConfig?.currencyCode, 'symbol', '1.0-0');
  }

  private setMinMaxPriceBasedOnStringPattern(minMaxPrice: string) {
    const [minPrice, maxPrice]: string[] = minMaxPrice.split('-');

    this.selectedMinPrice = !minPrice ? this.priceSliderOptions.floor : +minPrice;
    this.selectedMaxPrice = !maxPrice ? this.priceSliderOptions.ceil : +maxPrice;
  }

  ngOnDestroy() {
    if (!this.keepMenu) this.searchService.menuMobileOpen.next({ open: false, menu: null, top: null });
  }
}

interface PriceOption {
  minValue: number;
  maxValue: number;
  price: string;
  title: string;
}
